import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AdminLayout } from '../../layouts/AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useClient, useDeleteClient, useCases } from '../../hooks';
import { ClientAvatar } from '../../components/atoms/ClientAvatar';
import { ClientStatusBadge } from '../../components/atoms/ClientStatusBadge';
import { CasesTable } from '../../components/organisms/CasesTable';
import { LoadingOverlay } from '../../components/LoadingOverlay';
import { Button } from '../../components/atoms/Button';
import { toast } from '../../utils/toast';

export const ClientDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { client, loading, error } = useClient(id);
  const { deleteClient, loading: deleting } = useDeleteClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Fetch cases for this client (include unpublished for admin)
  const { data: casesData, loading: casesLoading } = useCases({
    initialFilters: {
      clientId: id,
      includeUnpublished: true,
      page: 1,
      limit: 100, // Show all cases for this client
      sortBy: 'createdAt',
      sortOrder: 'DESC',
    },
    autoFetch: !!id,
  });

  const isAdmin =
    user?.role === 'SUPER_ADMIN' ||
    user?.role === 'ADMIN' ||
    user?.role === 'admin';

  const handleDelete = async () => {
    if (!client) return;

    try {
      await deleteClient(client.id);
      toast.success('Client deleted successfully');
      navigate('/admin/clients');
    } catch (error) {
      console.error('Failed to delete client:', error);
      toast.error('Failed to delete client');
    }
  };

  const formatDate = (date: string | Date) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <LoadingOverlay isLoading={true} />
      </AdminLayout>
    );
  }

  if (error || !client) {
    return (
      <AdminLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <h3 className="text-sm font-medium text-red-800">
              {error ? 'Error loading client' : 'Client not found'}
            </h3>
            <p className="mt-1 text-sm text-red-700">
              {error?.message || 'The requested client could not be found.'}
            </p>
            <Link
              to="/admin/clients"
              className="mt-2 inline-block text-sm font-medium text-red-800 hover:text-red-700"
            >
              ← Back to clients
            </Link>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-8" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link
                to="/admin/clients"
                className="text-gray-500 hover:text-gray-700"
              >
                Clients
              </Link>
            </li>
            <li>
              <svg
                className="w-5 h-5 text-gray-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </li>
            <li>
              <span className="text-gray-900 font-medium">{client.name}</span>
            </li>
          </ol>
        </nav>

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <ClientAvatar name={client.name} logo={client.logo} size="lg" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {client.name}
                </h1>
                {client.industry && (
                  <p className="text-lg text-gray-600 mt-1">
                    {client.industry}
                  </p>
                )}
                <div className="mt-3">
                  <ClientStatusBadge
                    isActive={client.isActive}
                    isFeatured={client.isFeatured}
                  />
                </div>
              </div>
            </div>

            {isAdmin && (
              <div className="flex items-center space-x-3">
                <Button
                  variant="primary"
                  size="md"
                  onClick={() =>
                    navigate(`/admin/cases/new?clientId=${client.id}`)
                  }
                >
                  + New Case
                </Button>
                <button
                  onClick={() => navigate(`/admin/clients/${client.id}/edit`)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Edit Client
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Basic Information
            </h2>

            <dl className="space-y-4">
              {client.website && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Website</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <a
                      href={client.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:text-indigo-500 flex items-center"
                    >
                      {client.website}
                      <svg
                        className="w-4 h-4 ml-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </a>
                  </dd>
                </div>
              )}

              {client.description && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Description
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {client.description}
                  </dd>
                </div>
              )}

              {client.projectCount !== undefined && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Projects
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {client.projectCount} projects
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Contact Information
            </h2>

            <dl className="space-y-4">
              {client.contactName && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Contact Name
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {client.contactName}
                  </dd>
                </div>
              )}

              {client.contactEmail && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Email</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <a
                      href={`mailto:${client.contactEmail}`}
                      className="text-indigo-600 hover:text-indigo-500"
                    >
                      {client.contactEmail}
                    </a>
                  </dd>
                </div>
              )}

              {client.contactPhone && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Phone</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <a
                      href={`tel:${client.contactPhone}`}
                      className="text-indigo-600 hover:text-indigo-500"
                    >
                      {client.contactPhone}
                    </a>
                  </dd>
                </div>
              )}

              {(client.address || client.city || client.country) && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Address</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {[client.address, client.city, client.country]
                      .filter(Boolean)
                      .join(', ')}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Metadata */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 lg:col-span-2">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Metadata</h2>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Created</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDate(client.createdAt)}
                </dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-gray-500">
                  Last Updated
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDate(client.updatedAt)}
                </dd>
              </div>

              {client.createdBy && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Created By
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {client.createdBy.name} ({client.createdBy.email})
                  </dd>
                </div>
              )}

              <div>
                <dt className="text-sm font-medium text-gray-500">Client ID</dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono">
                  {client.id}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Cases Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-medium text-gray-900">
              Cases ({casesData?.data?.pagination?.total || 0})
            </h2>
            {isAdmin && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  navigate(`/admin/cases/new?clientId=${client.id}`)
                }
              >
                + Add Case
              </Button>
            )}
          </div>

          {casesLoading ? (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">Loading cases...</p>
            </div>
          ) : casesData?.data?.items && casesData.data.items.length > 0 ? (
            <CasesTable
              cases={casesData.data.items}
              loading={casesLoading}
              selectedIds={[]}
              onSelectCase={() => {}}
              onSelectAll={() => {}}
              onEdit={
                isAdmin ? (id) => navigate(`/admin/cases/${id}/edit`) : () => {}
              }
              onDelete={() => {}}
              onView={(id) => navigate(`/admin/cases/${id}/edit`)}
            />
          ) : (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500 mb-4">
                No cases found for this client.
              </p>
              {isAdmin && (
                <Button
                  variant="primary"
                  size="md"
                  onClick={() =>
                    navigate(`/admin/cases/new?clientId=${client.id}`)
                  }
                >
                  + Create First Case
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Delete Client
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Are you sure you want to delete &ldquo;{client.name}&rdquo;?
                This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
